import { ColDef, GridOptions, GridReadyEvent } from '@ag-grid-community/core';
import AgGridSolid, { AgGridSolidRef } from '@ag-grid-community/solid';
import { ExcelSheetIcon } from '@assets';
import { Box, Checkbox, Stack } from '@atgfr8/atg-ui-tailwind';
import { BasicModal, ToastType } from '@components';
import { Button } from '@components/Button';
import { DocumentEntityType } from '@components/FileUpload';
import CommonFileUpload from '@components/FileUpload/CommonFileUpload';
import { useParams } from '@solidjs/router';
import {
  getPricingData,
  PricingSheetModel,
  pricingStore,
  uploadPricingSheet,
} from '@store/customers/customerDetails/pricingTab';
import { closeModal, openModal } from '@store/modals';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast, isFullAdmin } from '@utils/utils';
import { Accessor } from 'solid-js';

import { pricingSheetGrid as classes } from './classes';

type PricingSheetGridProps = {
  pricingSheetColumnDefs: ColDef[];
  items: PricingSheetModel[];
  loading?: boolean;
  gridOptions?: GridOptions;
  onGridReady?: (event: GridReadyEvent) => void;
  handleToglePricing: (checked: boolean) => void;
  isToggleLoading?: boolean;
  pricingWarningOpen?: Accessor<boolean>;
  pricingFileUploadProps: {
    documentEntityType: DocumentEntityType;
    entityId: number;
  };
};

const PricingSheetGrid = (props: PricingSheetGridProps) => {
  let gridRef: AgGridSolidRef | undefined;
  const pricingUploadSheetModalId = 'pricingSheetUploadUpdateModalId';

  const { id } = useParams();

  const handleFileChange = async (file: File) => {
    const res = await uploadPricingSheet(id as string, file);
    if (res && res.isSuccess) {
      handleToast(ToastType.Success, 'Pricing file uploaded successfully');
      await getPricingData(Number(id));
    }
    closeModal(pricingUploadSheetModalId);
  };

  return (
    <Stack class={classes.stackStyle}>
      <Box class={classes.exportBtnBox}>
        <Box class={classes.topBox}>
          <Box class={classes.togglePricingStyle}>
            <Checkbox
              size="xs"
              color="info"
              class="bg-[#D32F2F4D] w-fit pr-3 rounded-md border border-[#B00020] m-0 ml-1!"
              label="Toggle Pricing"
              checked={pricingStore.isPricingEnabled}
              onChange={(checked) => props.handleToglePricing(checked)}
              disabled={props.isToggleLoading}
              dataTestId="pricing-sheet-grid-toggle-pricing-checkboxInput"
            />
          </Box>
          <Box>
            <Button
              label={
                <div class={classes.uploadSheetBtn}>
                  <img src={ExcelSheetIcon} height="18px" alt="excelsheet" />
                  Upload Pricing Sheet
                </div>
              }
              variant="contained"
              size="sm"
              onClick={() => openModal(pricingUploadSheetModalId)}
              dataTestId={formatDataTestId('Upload Pricing Sheet', 'button')}
            />
          </Box>
        </Box>
      </Box>
      <Box class={classes.muiGridStyle}>
        <AgGridSolid
          noRowsOverlayComponent={'No data available'}
          ref={gridRef}
          columnDefs={props.pricingSheetColumnDefs}
          rowData={props.items}
          gridOptions={{
            defaultColDef: {
              flex: 1,
            },
            masterDetail: true,
            suppressPaginationPanel: true,
            rowGroupPanelShow: 'never',
            rowHeight: 50,
          }}
        />
      </Box>
      <BasicModal
        id={pricingUploadSheetModalId}
        title="Upload Pricing Sheet"
        footer={false}
        onClose={() => {
          closeModal(pricingUploadSheetModalId);
        }}
        noCloseOnClickOutside={false}
      >
        <CommonFileUpload
          height={5}
          acceptedFileTypes={['csv']}
          shouldUploadOnSelect
          apiCallBackFn={handleFileChange}
          maxFileSize={5242880}
          fileRestrictionMessage="From here you can bulk import your pricing. The file must be a .csv and 5MB or less."
          isAdmin={isFullAdmin()}
        />
      </BasicModal>
    </Stack>
  );
};

export default PricingSheetGrid;
